"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Vector3 = void 0;
const lerpNumber_1 = require("../Utilities/lerpNumber");
class Vector3 {
    constructor(x, y, z) {
        this.x = x;
        this.y = y;
        this.z = z;
    }
    /**
     * Creates a zero vector
     * @returns Vector [0,0,0]
     */
    static Zero() {
        return new Vector3(0, 0, 0);
    }
    /**
     * Creates a vector of ones
     * @returns Vector [1,1,1]
     */
    static One() {
        return new Vector3(1, 1, 1);
    }
    /**
     * Creates a Right vector (positive X)
     * @param length optional length of the right unit. Default is 1
     * @returns Vector [length, 0,0]
     */
    static Right(length = 1) {
        return new Vector3(length, 0, 0);
    }
    /**
     * Creates a Left vector (negative X)
     * @param length optional length of the left unit. Default is 1
     * @returns Vector [-length, 0, 0]
     */
    static Left(length = 1) {
        return new Vector3(-length, 0, 0);
    }
    /**
     * Creates an Up vector (positive Y)
     * @param length optional length of the vector. Default is 1
     * @returns Vector [0, length, 0]
     */
    static Up(length = 1) {
        return new Vector3(0, length, 0);
    }
    /**
     * Creates a Down vector (negative Y)
     * @param length optional length of the vector. Default is 1
     * @returns Vector [0, -length, 0]
     */
    static Down(length = 1) {
        return new Vector3(0, -length, 0);
    }
    /**
     * Creates a Forward vector (positive Z)
     * @param length optional length of the vector. Default is 1
     * @returns Vector [0, 0, length]
     */
    static Forward(length = 1) {
        return new Vector3(0, 0, length);
    }
    /**
     * Creates a Backward vector (negative Z)
     * @param length optional length of the vector. Default is 1
     * @returns Vector [0, 0, -length]
     */
    static Backward(length = 1) {
        return new Vector3(0, 0, -length);
    }
    /**
     * Creates a Vector from an array of components
     * @param values The x, y, z components as an array
     * @returns The Vector
     */
    static FromArray(values) {
        return new Vector3(values[0], values[1], values[2]);
    }
    /**
     * Creates a Vector from a Data Transfer Object
     * @param dto The Data Transfer Object
     * @returns The Vector
     */
    static FromDTO(dto) {
        return new Vector3(dto.x, dto.y, dto.z);
    }
    /**
     * Gets the magnitude of the Vector3
     * @returns the magnitude of the Vector3
     */
    get magnitued() {
        const xSqr = this.x * this.x;
        const ySqr = this.y * this.y;
        const zSqr = this.z * this.z;
        const magnitude = Math.sqrt(xSqr + ySqr + zSqr);
        return magnitude;
    }
    /**
     * Gets the unit vector for this Vector3
     * @returns the unit vector
     */
    get unit() {
        const mag = this.magnitued;
        if (mag === 0) {
            return Vector3.Zero();
        }
        const unitX = this.x / mag;
        const unitY = this.y / mag;
        const unitZ = this.z / mag;
        return new Vector3(unitX, unitY, unitZ);
    }
    /**
     * Gets the vector as an array
     * @returns an array representing the vector
     */
    get array() {
        return [this.x, this.y, this.z];
    }
    /**
     * Get the vector as a Data Transfer Object
     */
    get dto() {
        return { x: this.x, y: this.y, z: this.z };
    }
}
exports.Vector3 = Vector3;
/**
 * Adds Vector B to Vector A
 * @param a Vector A
 * @param b Vector B
 * @return Result of A + B
 */
Vector3.Add = (a, b) => {
    return new Vector3(a.x + b.x, a.y + b.y, a.z + b.z);
};
/**
 * Subtracts Vector B from Vector A
 * @param a Vector A
 * @param b Vector B
 * @return Result of A - B
 */
Vector3.Subtract = (a, b) => {
    return new Vector3(a.x - b.x, a.y - b.y, a.z - b.z);
};
/**
 * Checks to see if the two vectors are equal
 * @param a Vector A
 * @param b Vector B
 * @return True if the two vectors are equal, otherwise false
 */
Vector3.Equal = (a, b) => {
    if (a.x === b.x && a.y === b.y && a.z === b.z)
        return true;
    else
        return false;
};
/**
 * Checks to see if the two vectors are close
 * @param a Vector A
 * @param b Vector B
 * @param tolerance Tolerance for checking. Defaulted to 0.01
 * @return True if the distance between the two vectors is less than the tolerance, otherwise false
 */
Vector3.Close = (a, b, tolerance = 0.01) => {
    const diff = Vector3.Subtract(a, b);
    const diffMag = diff.magnitued;
    if (diffMag < tolerance)
        return true;
    else
        return false;
};
/**
 * Calculates the Cross Product between two vectors
 * @param a Vector A
 * @param b Vector B
 * @return the cross product between the two vectors
 */
Vector3.Cross = (a, b) => {
    const x = a.y * b.z - a.z * b.y;
    const y = a.z * b.x - a.x * b.z;
    const z = a.x * b.y - a.y * b.x;
    return new Vector3(x, y, z);
};
/**
 * Calculates the Dot Product between two vectors
 * @param a Vector A
 * @param b Vector B
 * @return the dot product between the two vectors
 */
Vector3.Dot = (a, b) => {
    return a.x * b.x + a.y * b.y + a.z * b.z;
};
/**
 * Create a new vector that will have the same unit as the original vector but set to the desired length
 * @param vec The original vector
 * @param length Desired lenght of the final vector
 * @return The resulting vector
 */
Vector3.NewVectorOfLength = (vec, length) => {
    const unit = vec.unit;
    return new Vector3(length * unit.x, length * unit.y, length * unit.z);
};
Vector3.Transform = (vector, matrix) => {
    const m = matrix.m;
    const rx = vector.x * m[0] + vector.y * m[4] + vector.z * m[8] + m[12];
    const ry = vector.x * m[1] + vector.y * m[5] + vector.z * m[9] + m[13];
    const rz = vector.x * m[2] + vector.y * m[6] + vector.z * m[10] + m[14];
    const rw = 1 / (vector.x * m[3] + vector.y * m[7] + vector.z * m[11] + m[15]);
    const x = rx * rw;
    const y = ry * rw;
    const z = rz * rw;
    return new Vector3(x, y, z);
};
/**
 * Linearly inerpolates between two vectors
 * @param initial The starting point
 * @param final The end point
 * @param percent Percent of the lerp. 0 will return initial and 1 will return final
 * @return the LERP result Vector3
 */
Vector3.Lerp = (initial, final, percent) => {
    const x = lerpNumber_1.lerpNumber(initial.x, final.x, percent);
    const y = lerpNumber_1.lerpNumber(initial.y, final.y, percent);
    const z = lerpNumber_1.lerpNumber(initial.z, final.z, percent);
    return new Vector3(x, y, z);
};
